// useCurrentDojo.ts - ENHANCED VERSION
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useEffectiveDojo } from './useEffectiveDojo';

interface Dojo {
  id: string;
  name: string;
  logo_url?: string;
  plan: string;
  is_active: boolean;
  trial_start?: string;
  join_code?: string;
}

export const useCurrentDojo = () => {
  const [dojo, setDojo] = useState<Dojo | null>(null);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const { effectiveDojoId } = useEffectiveDojo();

  useEffect(() => {
    const fetchCurrentDojo = async () => {
      try {
        // For super users not impersonating, don't fetch a specific dojo
        if (!user) {
          setDojo(null);
          setLoading(false);
          return;
        }

        // If no effective dojo (super user not impersonating), return null
        if (!effectiveDojoId) {
          setDojo(null);
          setLoading(false);
          return;
        }

        console.log('Fetching dojo for ID:', effectiveDojoId);

        const { data, error } = await supabase
          .from('dojos')
          .select('*')
          .eq('id', effectiveDojoId)
          .single();

        if (error || !data) {
          console.error('Error fetching dojo:', error);
          // If impersonating and can't find dojo, this is an error
          if (effectiveDojoId) {
            setDojo({
              id: effectiveDojoId,
              name: 'Unknown Dojo',
              plan: 'trial',
              is_active: false,
              trial_start: null,
              join_code: null
            });
          } else {
            setDojo(null);
          }
        } else {
          console.log('Dojo fetched successfully:', data.name);
          setDojo(data);
        }
      } catch (error) {
        console.error('Error fetching dojo:', error);
        setDojo(null);
      } finally {
        setLoading(false);
      }
    };

    fetchCurrentDojo();
  }, [user, effectiveDojoId]);

  return { dojo, loading };
};